import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import numeral from 'numeral';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useSubmitField } from '../../hooks/useSubmitField';
import { SubmitFormContext } from '../../context';

import s from './AllowFreeDownloadField.scss';

interface Props {
  defaultOverride?: boolean;
  className?: string;
  disabled?: boolean;
}

const AllowFreeDownloadField: React.FC<Props> = ({
  defaultOverride,
  className,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { initResponse } = useContext(SubmitFormContext) || {};
  const {
    id,
    field: { onChange, value },
  } = useSubmitField('allowFreeDownload', {
    initAdapter: response =>
      defaultOverride ?? response.deviation?.isDownloadable ?? false,
    submitAdapter: ({ allowFreeDownload }) => ({
      allow_free_download: allowFreeDownload,
    }),
  });

  // If PCP is toggled on and source file is included this field will be disabled
  const { showAttachments, originalAttached, addWatermark } = useWatch({
    name: ['showAttachments', 'originalAttached', 'addWatermark'],
  });

  const deviationFileMetadata = initResponse?.deviation?.extended?.originalFile;

  return (
    <div className={className}>
      <ButtonFieldLayout
        label={t('deviationSubmit.form.allowFreeDownload.label.updated')}
        labelProps={{ id: `${id}-label`, htmlFor: id }}
        subLabel={
          deviationFileMetadata && (
            <div className={s['file-metadata']}>
              {deviationFileMetadata.type.toLocaleUpperCase()}{' '}
              {numeral(deviationFileMetadata.filesize).format('0b')}{' '}
              {deviationFileMetadata.width} x {deviationFileMetadata.height}
            </div>
          )
        }
      >
        <MainToggle
          variant="main"
          id={id}
          size={isMobile ? 'large' : 'small'}
          value={value}
          onChange={onChange}
          aria-labelledby={`${id}-label`}
          disabled={
            disabled || (showAttachments && originalAttached) || addWatermark
          }
        />
      </ButtonFieldLayout>
    </div>
  );
};
AllowFreeDownloadField.displayName = 'AllowFreeDownloadField';

export default AllowFreeDownloadField;
